// Purpose: AST sign-in page. Full-bleed composition per the approved
// design-preview mockup at apps/web/public/design-preview/login.html
// (iteration 2), with the three polish adjustments from scope §Part C:
//   1. Light-mode padlock opacity 0.025 → 0.015 (handled via CSS var).
//   2. Compliance banner color bumped for light mode (handled via
//      --compliance-banner-fg token).
//   3. Subhead max-width pinned to 560px to prevent "promises." orphan.
//
// Rendered OUTSIDE the authenticated <Layout> shell so the hero chrome
// (logo + theme toggle + status pill + orbital mesh) is full-bleed.
// See App.tsx for the routing structure that keeps this route
// standalone.

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  GridPattern,
  OrbitalMesh,
  PadlockWatermark,
  RadialGradientBg,
} from '../components/atmosphere/index.ts';
import { ComplianceBanner } from '../components/ComplianceBanner.tsx';
import { AppHeader } from '../components/nav/AppHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { StatusPill } from '../components/ui/StatusPill.tsx';
import { ThemeToggle } from '../components/ui/ThemeToggle.tsx';
import { ArrowRightIcon, CheckIcon } from '../components/icons/index.tsx';
import { SystemUseGate } from '../components/legal/SystemUseGate.tsx';

const ERROR_COPY: Record<string, string> = {
  domain_rejected:
    'That email address is not authorized to use AST. Sign in with your Apogee account.',
  invalid_state: 'Login did not complete. Please try again.',
  invalid_nonce: 'Login could not be verified. Please try again.',
  token_validation_failed: 'Your identity provider returned an invalid response. Please try again.',
  provisioning_failed: 'We could not set up your account. Contact IT if this keeps happening.',
  idp_error: 'Your identity provider returned an error. Please try again.',
};

const APP_VERSION = (import.meta.env.VITE_AST_API_VERSION as string | undefined) ?? 'dev';

export function Login() {
  const [params] = useSearchParams();
  const errorCode = params.get('error');
  const errorMessage = useMemo(
    () => (errorCode ? (ERROR_COPY[errorCode] ?? 'Sign-in failed.') : null),
    [errorCode]
  );
  const [gateOpen, setGateOpen] = useState(false);

  return (
    <div
      className={[
        'relative h-screen w-screen overflow-hidden',
        'bg-bg-primary text-fg-primary',
      ].join(' ')}
    >
      {/* Atmospheric background stack (z-0). Each layer is absolutely
          positioned inside this <div>. */}
      <div aria-hidden className="absolute inset-0 z-0">
        <RadialGradientBg />
        <GridPattern />
        <PadlockWatermark />
        <OrbitalMesh />
      </div>

      {/* Top navigation: logo left, theme toggle + status pill right. */}
      <AppHeader
        logoOnly
        rightSlot={
          <>
            <ThemeToggle />
            <StatusPill tone="success">All Systems Operational</StatusPill>
          </>
        }
      />

      {/* Hero content centered vertically + pushed to the left. */}
      <main
        className={['absolute top-1/2 left-20 -translate-y-1/2 z-10', 'max-w-[640px]'].join(' ')}
      >
        <div className="text-label uppercase text-accent-primary mb-5">
          Secure File Transfer Platform
        </div>

        <h1
          className={[
            'font-display text-display-xl text-fg-primary',
            'mb-6',
            '[font-feature-settings:var(--font-feature-display)]',
          ].join(' ')}
        >
          Mission-critical file transfer for{' '}
          <em className="italic text-fg-secondary">serious work</em>.
        </h1>

        <p className={['text-body-l text-fg-secondary mb-10 max-w-[560px]'].join(' ')}>
          End-to-end encrypted with hash-chained audit. Built for defense contractors and regulated
          industries that need proof, not promises.
        </p>

        {errorMessage ? (
          <div
            role="alert"
            data-testid="login-error"
            className={[
              'mb-6 inline-flex items-center gap-2',
              'bg-status-danger-muted text-status-danger',
              'border border-status-danger/20',
              'rounded-md px-4 py-3 text-body-s',
            ].join(' ')}
          >
            {errorMessage}
          </div>
        ) : null}

        <div>
          <Button
            variant="primary"
            size="xl"
            rightIcon={<ArrowRightIcon size={16} strokeWidth={2.5} />}
            onClick={() => {
              setGateOpen(true);
            }}
          >
            Sign in with Apogee SSO
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-6 mt-9 text-body-s text-fg-muted">
          <TrustMarker>AES-256-GCM encryption</TrustMarker>
          <TrustMarker>Azure Government hosted</TrustMarker>
          <TrustMarker>Hash-chained audit log</TrustMarker>
        </div>
      </main>

      {/* Banner: left label ("APOGEE ENGINEERING, LLC" via the env
          var), right side = version only. The org prop is dropped
          because the env-var label IS the org — passing both would
          render Apogee's name on both sides of the footer. */}
      <ComplianceBanner version={`v${APP_VERSION}`} />

      <SystemUseGate
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        onAcknowledge={() => {
          window.location.assign('/auth/login');
        }}
      />
    </div>
  );
}

function TrustMarker({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2">
      <CheckIcon size={14} className="text-status-success" />
      <span>{children}</span>
    </span>
  );
}
